// MO7.1, MO7.1M
import React, { useCallback, useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import {
  ThemeOptions,
  ThemeOverride,
} from '@wix/da-react-context/pkg/ThemeContext';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  BiData,
  SearchClickBiEvent,
  UserNameClickBiEvent,
} from '@wix/da-bi/pkg/events';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { Url } from '@wix/da-url';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import SearchBar from './SearchBar';
import QuickSearchList from './QuickSearch/QuickSearchList';

import s from './ShopPageHeader.scss';

const BG_ARTIST = {
  // note: if art changes, these need to change
  username: 'moxximonroe',
  userId: 46690854,
};

// TODO: when merging shop_header_updates
// - replace shop_header_updates in locale keys with shopPage.header

export interface Props {
  searchTerm?: string;

  // TODO: removable if/when shop_header_updates is merged
  searchPageType?: string;
  totalResults?: number;
}

/** shop page explanation text and search input, sits above subnav tabs */
const ShopPageHeader: React.FC<Props> = ({ searchTerm = '' }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const logBiEvent = useBiLogger();

  const submitCallback = useCallback(
    newSearchTerm => {
      logBiEvent(
        BiData<SearchClickBiEvent>({
          evid: 710,
          search_terms: newSearchTerm,
          sectionname: 'shop',
        })
      );

      window.location.assign(
        newSearchTerm
          ? getPathnameWithSearch(newSearchTerm)
          : getPathnameWithoutSearch()
      );
    },
    [logBiEvent]
  );

  return (
    <Section className={classnames(s['root'], searchTerm && s['with-search'])}>
      <ThemeOverride className={s['theme-override']} theme={ThemeOptions.LIGHT}>
        {!searchTerm && (
          <Heading className={s['title']}>
            {t('shop_header_updates.title')}
          </Heading>
        )}

        <SearchBar
          className={s['search-bar']}
          id="shop-search-bar"
          name="q"
          placeholder={
            isMobile
              ? t('shopPage.header.searchFieldPlaceholder.mobile')
              : t('shopPage.header.searchFieldPlaceholder')
          }
          initialValue={searchTerm}
          buttonText={t('shop_header_updates.searchButton')}
          onSubmit={submitCallback}
        />

        {!searchTerm && (
          <ul className={s['quick-search']}>
            <span className={s['quick-search-text']}>
              {t('shop_header_updates.quickSearchText')}
            </span>
            <QuickSearchList
              className={s['quick-search-list']}
              currSearch={searchTerm}
            />
          </ul>
        )}

        {!searchTerm && !isMobile && (
          <span className={s['attribution']}>
            {t('shop_header_updates.artBy')}{' '}
            <BiLink
              className={s['attribution-userlink']}
              href={Url.userLink(BG_ARTIST.username)}
              target="_blank"
              rel="noopener noreferrer"
              biData={BiData<UserNameClickBiEvent>({
                evid: 101,
                typeid: ResourceType.USER,
                itemid: BG_ARTIST.userId,
              })}
            >
              {BG_ARTIST.username}
            </BiLink>
          </span>
        )}
      </ThemeOverride>
    </Section>
  );
};

ShopPageHeader.displayName = 'ShopPageHeader';
export default ShopPageHeader;

// these are to handle the special /shop/search1=term vs /shop
// others: /shop/commissions/search1=term vs. /shop/commissions
function getPathnameWithoutSearch(): string {
  if (typeof window === 'undefined') {
    return '/shop';
  }
  const { pathname: windowPathname } = window.location;
  return windowPathname === '/shop/search'
    ? '/shop'
    : windowPathname.replace('/search', '');
}

function getPathnameWithSearch(searchTerm: string): string {
  if (typeof window === 'undefined') {
    return '/shop/search';
  }
  const { pathname: windowPathname } = window.location;
  const newPathname = windowPathname.endsWith('/search')
    ? windowPathname
    : `${windowPathname}/search`;
  return `${newPathname}?q=${encodeURIComponent(searchTerm)}`;
}
